import { useRef, useState } from "react";
import { Upload, Loader2, Image as ImageIcon } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable image upload component for admin forms.
 * Uploads to DigitalOcean Spaces via /api/admin/upload.
 * Existing URLs (e.g. Unsplash, CDN) continue to work via the URL input.
 *
 * @param {Object} props
 * @param {string} props.value - Current image URL
 * @param {function} props.onChange - Called with new URL: (url) => void
 * @param {string} props.folder - Spaces folder: classes, events, instructors, studios, qr
 * @param {string} [props.label] - Field label
 * @param {string} [props.placeholder] - Placeholder for URL input
 */
export const ImageUpload = ({
  value = "",
  onChange,
  folder = "general",
  label = "Image",
  placeholder = "Upload a file or paste image URL",
}) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowed.includes(file.type)) {
      toast.error("Allowed: JPEG, PNG, WebP, GIF");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Max size: 5MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/admin/upload?folder=${folder}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res.data?.url;
      if (url) {
        onChange(url);
        toast.success("Image uploaded");
      } else {
        toast.error("Upload failed");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Upload failed";
      toast.error(typeof msg === "string" ? msg : "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <Label className="text-white">{label}</Label>
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Preview */}
        <div className="flex-shrink-0 w-full sm:w-24 h-24 rounded-lg border border-neutral-700 bg-neutral-900 overflow-hidden flex items-center justify-center">
          {value ? (
            <img
              src={value}
              alt="Preview"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextElementSibling?.classList.remove("hidden");
              }}
            />
          ) : null}
          <div
            className={`w-full h-full flex items-center justify-center text-neutral-500 ${
              value ? "hidden" : ""
            }`}
          >
            <ImageIcon className="w-8 h-8" />
          </div>
        </div>

        {/* Upload + URL */}
        <div className="flex-1 space-y-2">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="border-neutral-700 hover:bg-neutral-800 shrink-0"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              <span className="ml-1">{uploading ? "Uploading…" : "Upload"}</span>
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="input-empire"
            placeholder={placeholder}
          />
        </div>
      </div>
    </div>
  );
};
